// src/views/admin/ADCaisse.jsx
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { usePermissions } from "../../context/PermissionsContext";
import ConfigCaisse from "./caisse/ADCaisseConfig";
import CaissesMouvements from "./caisse/ADCaisseCaissesMouvements";
import SituationVersements from "./caisse/ADCaisseSituationVersements";
import Convocations from "./caisse/ADCaisseConvocations";
import BilanCaisse from "./caisse/ADCaisseBilan";


/* ============================================================================
 * PERMISSIONS (module "caisse") — même schéma que ADEleves.jsx :
 * - caisse.voir_bilan           : onglet "Bilan" + son contenu
 * - caisse.exporter_bilan       : boutons PDF / impression dans le Bilan
 * - caisse.configurer           : onglet "Configuration" + gestion des
 *                                  caisses / rubriques / modes de paiement
 *                                  dans l'onglet "Caisses & Mouvements"
 * - caisse.mouvementer          : encaissement / décaissement / transfert
 * - caisse.gerer_sessions       : ouvrir / fermer une session de caisse
 * - caisse.gerer_clotures       : créer / valider une clôture de caisse
 * - caisse.affecter_utilisateurs: affecter des utilisateurs à une caisse
 * - caisse.voir_situation       : onglet "Scolarités" (situation & versements)
 * - caisse.exporter_situation   : boutons PDF / impression dans "Scolarités"
 * - caisse.encaisser_versement  : bouton "Nouveau versement"
 * - caisse.annuler_versement    : bouton "Annuler" sur un versement
 * - caisse.gerer_remises        : bouton "Nouvelle remise"
 * - caisse.gerer_convocations   : onglet "Convocations"
 *
 * L'onglet "Caisses & Mouvements" regroupe plusieurs sous-fonctions
 * (configuration des caisses/rubriques/modes de paiement + mouvements +
 * sessions + clôtures + affectations) : il reste visible dès que
 * l'utilisateur a AU MOINS une des permissions qui s'y rapportent. Le détail
 * fin (quel bouton, quel sous-onglet) est géré à l'intérieur de
 * ADCaisseCaissesMouvements.jsx.

 * ========================================================================== */
export default function ADCaisse({ slug }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { canSee } = usePermissions();

  const peutVoirBilan       = canSee("caisse.voir_bilan");
  const peutConfigurer      = canSee("caisse.configurer");
  const peutMouvementer     = canSee("caisse.mouvementer");
  const peutGererSessions   = canSee("caisse.gerer_sessions");
  const peutGererClotures   = canSee("caisse.gerer_clotures");
  const peutAffecterUsers   = canSee("caisse.affecter_utilisateurs");
  const peutVoirSituation   = canSee("caisse.voir_situation");
  const peutGererConvocations = canSee("caisse.gerer_convocations");

  // Onglet "Caisses & Mouvements" visible si au moins une des permissions
  // qui s'y rattachent est accordée.
  const peutVoirCaissesMouvements =
    peutConfigurer || peutMouvementer || peutGererSessions || peutGererClotures || peutAffecterUsers;

  const ONGLETS = [
    { key: "bilan", label: "BILAN", icon: "ti-report", visible: peutVoirBilan },
    { key: "config", label: "CONFIGURATION", icon: "ti-settings", visible: peutConfigurer },
    { key: "caisses", label: "CAISSES & MOUVEMENTS", icon: "ti-cash", visible: peutVoirCaissesMouvements },
    { key: "situation", label: "SCOLARITÉS", icon: "ti-report-money", visible: peutVoirSituation },
    { key: "convocations", label: "CONVOCATIONS", icon: "ti-mail", visible: peutGererConvocations },
  ].filter(o => o.visible);


  const CLES_VALIDES = ONGLETS.map(o => o.key);

  const tabParam = searchParams.get("tab");
  const [onglet, setOnglet] = useState(
    CLES_VALIDES.includes(tabParam) ? tabParam : (CLES_VALIDES[0] || null)
  );

  const changerOnglet = (key) => {
    setOnglet(key);
    // Garde l'URL synchronisée (permet de partager/rafraîchir sur le bon onglet).
    // On préserve les autres paramètres éventuels (ex: ?inscription=...).
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (key === CLES_VALIDES[0]) next.delete("tab");
      else next.set("tab", key);
      return next;
    });
  };

  if (ONGLETS.length === 0) {
    return (
      <div className="card" style={{ textAlign: "center", color: "var(--h)", padding: 40 }}>
        Vous n'avez accès à aucune section du module Caisse.
      </div>
    );
  }

  return (
    <div>
      <div className="sub-tab-nav">
        {ONGLETS.map(o => (
          <button
            key={o.key}
            className={`sub-tab-btn ${onglet === o.key ? "active" : ""}`}
            onClick={() => changerOnglet(o.key)}
          >
            <i className={`ti ${o.icon}`} /> <span>{o.label}</span>
          </button>
        ))}
      </div>
      {onglet === "bilan" && peutVoirBilan && <BilanCaisse slug={slug} />}
      {onglet === "config" && peutConfigurer && <ConfigCaisse slug={slug} />}
      {onglet === "caisses" && peutVoirCaissesMouvements && <CaissesMouvements slug={slug} />}
      {onglet === "situation" && peutVoirSituation && <SituationVersements slug={slug} />}
      {onglet === "convocations" && peutGererConvocations && <Convocations slug={slug} />}

      <style>{`
        /* ─── ONGLETS (thème bleu + or) ─── */
        .sub-tab-nav {
          display: flex;
          gap: 4px;
          flex-wrap: wrap;
          background: linear-gradient(180deg, var(--pr-dk) 0%, var(--pr) 100%);
          border-radius: 10px;
          border-bottom: 3px solid var(--or);
          padding: 8px 12px;
          margin-bottom: 16px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .sub-tab-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 18px;
          border: none;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          background: transparent;
          color: rgba(255,255,255,0.75);
          font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }

        .sub-tab-btn i { font-size: 18px; }

        .sub-tab-btn:hover {
          background: rgba(255,255,255,0.12);
          color: var(--or-lt);
        }

        .sub-tab-btn.active {
          background: white;
          color: var(--pr);
          box-shadow: inset 0 -3px 0 var(--or), 0 2px 8px rgba(0,0,0,0.15);
        }
        .sub-tab-btn.active i { color: var(--or); }
        .sub-tab-btn.active:hover { background: white; color: var(--pr); }

        @media (max-width: 768px) {
          .sub-tab-btn { padding: 6px 10px; font-size: 11px; }
          .sub-tab-btn i { font-size: 14px; }
        }
        @media (max-width: 576px) {
          .sub-tab-btn span { display: none; }
          .sub-tab-btn i { font-size: 18px; margin: 0; }
        }
      `}</style>
    </div>
  );
}